import { useState } from 'react';
import {
  Award,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCcw,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Clock,
  BookOpen,
  FileCheck,
  BarChart3
} from 'lucide-react';
import type {
  FinalizeExamResponse,
  Question,
  QuestionClientView
} from '@shared/types';

interface ExamReportViewProps {
  report: FinalizeExamResponse;
  questions: (Question | QuestionClientView)[];
  userAnswers?: Record<string, string>;
  onRetake: () => void;
  onReturnDashboard: () => void;
}

// Open-Book Binder chapters recommended per domain for remediation
const BINDER_RECOMMENDATIONS: Record<string, string> = {
  'cpgp-dom-1': 'Binder: 21 CFR 210/211 Subparts A-B & CPGM 7356.002 (FDA 483 response protocols)',
  'cpgp-dom-2': 'Binder: ICH Q10 Quality System & 21 CFR Part 7 (Drug Recall Classifications I/II/III)',
  'cpgp-dom-3': 'Binder: FDA OOS Guidance (Phase I Lab vs Phase II Mfg) & USP <621> SST bracketing',
  'cpgp-dom-4': 'Binder: EU GMP Annex 1 (2022) Section 8 & Contamination Control Strategy (CCS)',
  'cpgp-dom-5': 'Binder: FDA 2011 Process Validation Lifecycle (PPQ/CPV) & 21 CFR Part 11 ALCOA+',
  'rac-dom-1': 'Binder: FD&C Act 506(a) Breakthrough Therapy & 21 CFR 316 Orphan Drug Exclusivity',
  'rac-dom-2': 'Binder: 21 CFR 312.40 IND 30-Day Clock & 312.32 (15-Day SUSAR vs 7-Day Fatal)',
  'rac-dom-3': 'Binder: 21 CFR 314.50 NDA / 505(b)(2) vs 505(j) ANDA & ICH M4 CTD Modules',
  'rac-dom-4': 'Binder: 21 CFR 314.70 Post-Approval CMC (PAS vs CBE-30) & 314.81 Field Alert Reports'
};

export function ExamReportView({
  report,
  questions,
  userAnswers = {},
  onRetake,
  onReturnDashboard
}: ExamReportViewProps) {
  const [activeTab, setActiveTab] = useState<'domains' | 'review'>('domains');
  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(null);

  const passingThreshold = 70.0;
  const isPassed = report.score >= passingThreshold;

  // Official ASQ Scaled Score calculation (100 to 750 scale, passing cutoff = 550)
  const rawScore = report.score;
  const asqScaledScore = rawScore < 70
    ? Math.max(100, Math.min(549, Math.round(100 + (rawScore / 70) * 450)))
    : Math.min(750, Math.round(550 + ((rawScore - 70) / 30) * 200));

  // Format time (seconds to MM:SS or HH:MM:SS)
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      return `${hours}h ${remainingMins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  // Find incorrect questions for review
  const reviewableQuestions = questions.map((q, index) => {
    const qWithDetails = q as Question;
    const selected = userAnswers[q.id];
    const isCorrect = qWithDetails.correct_option ? selected === qWithDetails.correct_option : null;
    return {
      question: qWithDetails,
      questionIndex: index + 1,
      selectedOption: selected,
      isCorrect
    };
  });

  const incorrectItems = reviewableQuestions.filter(item => item.isCorrect === false);

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn pb-16">
      {/* Top Banner: Pass / Fail Status & ASQ Scaled Score */}
      <div
        className={`relative overflow-hidden rounded-3xl border p-6 sm:p-8 shadow-sm dark:shadow-2xl backdrop-blur transition-colors duration-200 ${
          isPassed
            ? 'bg-gradient-to-br from-emerald-50/90 via-white to-emerald-50/50 dark:from-slate-900 dark:via-emerald-950/40 dark:to-slate-900 border-emerald-200/80 dark:border-emerald-500/40 shadow-emerald-500/5 dark:shadow-emerald-950/20'
            : 'bg-gradient-to-br from-rose-50/90 via-white to-rose-50/50 dark:from-slate-900 dark:via-rose-950/40 dark:to-slate-900 border-rose-200/80 dark:border-rose-500/40 shadow-rose-500/5 dark:shadow-rose-950/20'
        }`}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                  isPassed
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400'
                }`}
              >
                {isPassed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                <span>{isPassed ? 'REGULATORY BENCHMARK MET' : 'REMEDIATION REQUIRED'}</span>
              </span>

              <span className="text-xs font-mono font-bold bg-white/80 dark:bg-slate-950/70 border border-slate-200 dark:border-slate-800 text-indigo-700 dark:text-indigo-300 px-2.5 py-1 rounded-full">
                ASQ Scaled: {asqScaledScore} / 750 (Pass Cutoff: 550)
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
              {isPassed ? 'Examination Passed' : 'Target Threshold Not Reached'}
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-xl leading-relaxed">
              {isPassed
                ? `Congratulations! Your raw score of ${report.score}% converts to an ASQ Scaled Score of ${asqScaledScore}/750, comfortably above the official 550 passing mark across all Body of Knowledge domains.`
                : `Your raw score of ${report.score}% converts to an ASQ Scaled Score of ${asqScaledScore}/750, below the official 550 passing threshold. Focus on the deficient domains highlighted below.`}
            </p>
          </div>

          {/* Large Score Metric Card */}
          <div className="flex items-center space-x-4 bg-white/90 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 self-start md:self-auto shadow-sm dark:shadow-inner">
            <div className="text-center px-2">
              <div
                className={`text-4xl sm:text-5xl font-extrabold font-mono tracking-tight ${
                  isPassed ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                }`}
              >
                {asqScaledScore}
              </div>
              <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500 dark:text-slate-400 mt-1">
                Scaled Score (Pass: 550)
              </div>
              <div className="text-xs font-mono font-bold text-slate-700 dark:text-slate-300 mt-0.5">
                Raw: {report.score}%
              </div>
            </div>

            <div className="h-14 w-[1px] bg-slate-200 dark:bg-slate-800" />

            <div className="space-y-1.5 text-xs">
              <div className="text-slate-700 dark:text-slate-300 font-medium flex items-center gap-1.5">
                <span className="text-slate-500 dark:text-slate-400">Correct:</span>
                <span className="font-bold text-slate-900 dark:text-slate-100 font-mono">
                  {report.correct_count} / {report.total_questions}
                </span>
              </div>
              <div className="text-slate-700 dark:text-slate-300 font-medium flex items-center gap-1.5">
                <span className="text-slate-500 dark:text-slate-400">Time:</span>
                <span className="font-bold text-slate-900 dark:text-slate-100 font-mono flex items-center gap-1">
                  <Clock className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                  {formatTime(report.total_time_seconds || 0)}
                </span>
              </div>
              <div className="text-slate-700 dark:text-slate-300 font-medium flex items-center gap-1.5">
                <span className="text-slate-500 dark:text-slate-400">Pacing:</span>
                <span className="font-bold text-slate-900 dark:text-slate-100 font-mono">
                  {Math.round((report.total_time_seconds || 0) / Math.max(1, report.total_questions))}s / Q
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons: Retake vs Return */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-2 bg-slate-100 dark:bg-slate-900/60 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
          <button
            onClick={() => setActiveTab('domains')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 ${
              activeTab === 'domains'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20 dark:shadow-indigo-950'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>BOK Domain Breakdown</span>
          </button>

          <button
            onClick={() => setActiveTab('review')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-2 ${
              activeTab === 'review'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20 dark:shadow-indigo-950'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            <span>Review Items ({incorrectItems.length > 0 ? `${incorrectItems.length} Incorrect` : 'All'})</span>
          </button>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={onRetake}
            className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold transition border border-slate-200 dark:border-slate-700 flex items-center space-x-2 shadow-xs"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Retake Drill</span>
          </button>

          <button
            onClick={onReturnDashboard}
            className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition shadow-md shadow-indigo-600/20 dark:shadow-indigo-950 flex items-center space-x-2"
          >
            <span>Return to Dashboard</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* TAB 1: Domain-by-Domain Gap Analysis */}
      {activeTab === 'domains' && (
        <div className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-sm dark:shadow-xl space-y-6 animate-fadeIn transition-colors duration-200">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Award className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <span>Body of Knowledge (BOK) Performance</span>
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Domain-specific performance against the 70% competency benchmark
            </p>
          </div>

          <div className="space-y-4">
            {report.domain_breakdown.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">
                No domain breakdown data available for this session.
              </div>
            ) : (
              report.domain_breakdown.map(domain => {
                const isProficient = domain.score_percentage >= 80;
                const isBorderline = domain.score_percentage >= 70 && domain.score_percentage < 80;
                const isDeficient = domain.score_percentage < 70;
                const binderRecommendation = BINDER_RECOMMENDATIONS[domain.domain_id];

                let cardBg = 'bg-slate-50/70 dark:bg-slate-950/60 border-slate-200/80 dark:border-slate-800/80';
                let badgeText = 'Proficient';
                let badgeStyle = 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20';
                let scoreBadge = 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60';

                if (isBorderline) {
                  badgeText = 'Borderline';
                  badgeStyle = 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20';
                  scoreBadge = 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800/60';
                } else if (isDeficient) {
                  cardBg = 'bg-rose-50/60 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/40';
                  badgeText = 'Remediation Required';
                  badgeStyle = 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20';
                  scoreBadge = 'bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800/60';
                }

                return (
                  <div
                    key={domain.domain_id}
                    className={`p-4 sm:p-5 rounded-2xl border transition ${cardBg}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400">
                            {domain.domain_code}
                          </span>
                          <span className="text-sm font-semibold text-slate-900 dark:text-slate-200">
                            {domain.domain_name}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {domain.correct_count} of {domain.total_questions} questions correct
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 self-end sm:self-auto">
                        <span className={`text-xs font-mono font-bold px-2.5 py-1 rounded-lg border ${scoreBadge}`}>
                          {domain.score_percentage}%
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badgeStyle}`}>
                          {badgeText}
                        </span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          isProficient ? 'bg-emerald-500' : isBorderline ? 'bg-amber-500' : 'bg-rose-500'
                        }`}
                        style={{ width: `${Math.min(100, domain.score_percentage)}%` }}
                      ></div>
                    </div>

                    {/* Open-Book Binder Recommendation Callout */}
                    {binderRecommendation && (
                      <div className={`mt-3 pt-2.5 border-t text-xs flex items-start gap-2 ${
                        isDeficient
                          ? 'border-rose-200 dark:border-rose-900/40 text-rose-800 dark:text-rose-200'
                          : 'border-slate-200/80 dark:border-slate-800/80 text-slate-600 dark:text-slate-400'
                      }`}>
                        <BookOpen className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span className="leading-relaxed">
                          <strong>Open-Book Binder Index:</strong> {binderRecommendation}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* TAB 2: Review Incorrect / All Items */}
      {activeTab === 'review' && (
        <div className="space-y-4 animate-fadeIn">
          <div className="flex items-center justify-between pb-2">
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <span>Detailed Question Review</span>
            </h2>
            <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
              Showing {reviewableQuestions.length} Questions
            </span>
          </div>

          {reviewableQuestions.map(item => {
            const isExpanded = expandedQuestionId === item.question.id;
            const hasFullDetails = !!item.question.scenario_stem;

            return (
              <div
                key={item.question.id}
                className="bg-white dark:bg-slate-900/90 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden transition shadow-sm dark:shadow-lg"
              >
                {/* Accordion Header */}
                <button
                  onClick={() => setExpandedQuestionId(isExpanded ? null : item.question.id)}
                  className="w-full p-4 sm:p-5 text-left flex items-start justify-between gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition"
                >
                  <div className="flex items-start space-x-3.5">
                    <span
                      className={`w-7 h-7 rounded-xl flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5 border ${
                        item.isCorrect === true
                          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 dark:border-emerald-500/40'
                          : item.isCorrect === false
                          ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30 dark:border-rose-500/40'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      {item.questionIndex}
                    </span>

                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {item.question.domain_code && (
                          <span className="text-[11px] font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                            {item.question.domain_code}
                          </span>
                        )}
                        {item.question.primary_citation && (
                          <span className="text-[11px] font-mono bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                            {item.question.primary_citation}
                          </span>
                        )}
                        {item.isCorrect === true && (
                          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-200 dark:border-emerald-800">
                            CORRECT
                          </span>
                        )}
                        {item.isCorrect === false && (
                          <span className="text-[10px] font-bold text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/80 px-2 py-0.5 rounded border border-rose-200 dark:border-rose-800">
                            INCORRECT
                          </span>
                        )}
                      </div>

                      <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-200 line-clamp-2 leading-relaxed pt-1">
                        {item.question.scenario_stem}
                      </p>
                    </div>
                  </div>

                  <div className="text-slate-400 p-1 flex-shrink-0">
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </button>

                {/* Accordion Body: Full Question & Rationale */}
                {isExpanded && hasFullDetails && (
                  <div className="p-5 sm:p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/70 space-y-5 animate-slideDown">
                    <div className="text-sm sm:text-base text-slate-900 dark:text-slate-100 leading-relaxed font-normal">
                      {item.question.scenario_stem}
                    </div>

                    {/* Options list */}
                    <div className="space-y-2.5">
                      {(['A', 'B', 'C', 'D'] as const).map(k => {
                        const optText = (item.question as any)[`option_${k.toLowerCase()}`];
                        const isChosen = item.selectedOption === k;
                        const isCorrectOption = item.question.correct_option === k;

                        let style = 'bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300';
                        if (isCorrectOption) {
                          style = 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-500 text-emerald-950 dark:text-emerald-100 font-medium';
                        } else if (isChosen && !isCorrectOption) {
                          style = 'bg-rose-50 dark:bg-rose-950/40 border-rose-500 text-rose-950 dark:text-rose-200 line-through decoration-rose-400/60';
                        }

                        return (
                          <div
                            key={k}
                            className={`p-3 rounded-xl border text-xs sm:text-sm flex items-start space-x-3 ${style}`}
                          >
                            <span
                              className={`w-6 h-6 rounded-lg flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5 border ${
                                isCorrectOption
                                  ? 'bg-emerald-600 text-white border-emerald-500 dark:bg-emerald-500 dark:text-slate-950 dark:border-emerald-400'
                                  : isChosen
                                  ? 'bg-rose-600 text-white border-rose-500 dark:bg-rose-500 dark:border-rose-400'
                                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              {k}
                            </span>
                            <span className="flex-1 leading-relaxed">{optText}</span>
                            {isCorrectOption && <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />}
                            {isChosen && !isCorrectOption && <XCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />}
                          </div>
                        );
                      })}
                    </div>

                    {/* Rationales */}
                    <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3 text-xs sm:text-sm shadow-xs">
                      <div className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5 text-xs uppercase tracking-wider">
                        <FileCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        <span>Regulatory Analysis & Justification</span>
                      </div>

                      {item.question.correct_option && (
                        <div className="p-3 rounded-lg bg-emerald-50/80 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 text-emerald-950 dark:text-emerald-200">
                          <strong className="text-emerald-800 dark:text-emerald-300 block mb-1">
                            Why {item.question.correct_option} is compliant:
                          </strong>
                          {(item.question as any)[`rationale_${item.question.correct_option.toLowerCase()}`]}
                        </div>
                      )}

                      {/* Other option rationales */}
                      <div className="space-y-1.5 pt-1">
                        {(['A', 'B', 'C', 'D'] as const)
                          .filter(k => k !== item.question.correct_option)
                          .map(k => {
                            const rText = (item.question as any)[`rationale_${k.toLowerCase()}`];
                            if (!rText) return null;
                            return (
                              <div key={k} className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                <strong className="text-slate-800 dark:text-slate-300">Option {k}:</strong> {rText}
                              </div>
                            );
                          })}
                      </div>

                      {/* Primary Citation Reference */}
                      <div className="pt-2 border-t border-slate-100 dark:border-slate-800 font-mono text-xs text-slate-500 dark:text-slate-400 flex items-center justify-between">
                        <span>Citation: {item.question.primary_citation}</span>
                        {item.question.secondary_citation && (
                          <span className="text-slate-400 dark:text-slate-500">{item.question.secondary_citation}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
